// TODO STUDIO MIGRATION (Phase 8): middleware /magic-element-engine/execute
// now returns { jobId, room } via the JobBus path. Migrate to
// enqueueMagicElementExecute() from server_utils.es6 +
// useJobSocket(jobId, onEvent) for the async LayerSet result.
// The existing /decompose polling path (used by decomposeBoxWithAideaSmartElements
// below) remains functional until the socket-driven migration is complete.

/**
 * Aidea Smart Elements client: triggers middleware's magic-element-engine
 * decomposition and polls until completion. Applies the resulting LayerSet
 * via the APPLY_AIDEA_SMART_ELEMENTS reducer.
 *
 * Studio-side identifiers remain `aideaSmartElements` (action types, file names,
 * i18n keys). The middleware endpoint is `magic-element-engine` — that
 * naming asymmetry is by design.
 */
import i18n from "i18next";
import { apiPost, apiGet } from "./http_service";
import { getToken } from "./environment_config";
import { assetKey } from "./utils";
import {
  applyAideaSmartElements,
  setAideaSmartElementsProcessing,
  clearAideaSmartElementsProcessing,
} from "./actions.es6";

function authHeaders() {
  const token = getToken();
  const h = { Accept: "application/json" };
  if (token) h.Authorization = "Bearer " + token;
  if (window.tenantId) h["Abp.TenantId"] = window.tenantId;
  return h;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 180; // 6 minutes worst case

/** Plugins that hold a usable image URL in `pluginToolbar.state.url`. */
export const ELIGIBLE_SOURCE_PLUGIN_IDS = ["Shapes", "HotspotImages"];

export function isEligibleForAideaSmartElements(pluginToolbar) {
  if (!pluginToolbar) return false;
  if (!ELIGIBLE_SOURCE_PLUGIN_IDS.includes(pluginToolbar.pluginId)) return false;
  const url = pluginToolbar.state && pluginToolbar.state.url;
  return typeof url === "string" && url.length > 0;
}

/**
 * Walk up the box's parent chain until we reach a navItem (slide / page /
 * section / contained-view). `box.parent` may point to another `bo-…` /
 * `bs-…` container rather than directly to the slide id, so a naive
 * `box.parent` won't find the slide in `navItemsById`.
 */
function resolveOwningNavId(boxesById, navItemsById, startBoxId) {
  let cursor = startBoxId;
  for (let i = 0; i < 32 && cursor != null; i++) {
    if (navItemsById && Object.prototype.hasOwnProperty.call(navItemsById, cursor)) {
      return cursor;
    }
    const b = boxesById && boxesById[cursor];
    if (!b) return cursor; // best-effort: return what we have, reducer will no-op
    cursor = b.parent;
  }
  return cursor;
}

function buildRequestBody(state, boxId) {
  const present = state.undoGroup && state.undoGroup.present ? state.undoGroup.present : state;
  const box = present.boxesById && present.boxesById[boxId];
  const tb = present.pluginToolbarsById && present.pluginToolbarsById[boxId];
  if (!box) throw new Error(`Box ${boxId} not found`);
  if (!isEligibleForAideaSmartElements(tb)) {
    throw new Error("Selected box is not an eligible image plugin (Shapes or HotspotImages)");
  }
  const navId = resolveOwningNavId(present.boxesById, present.navItemsById, box.parent);
  // eslint-disable-next-line no-console
  console.log("[AideaSmartElements] resolved navId", {
    sourceBoxId: boxId,
    immediateParent: box.parent,
    resolvedNavId: navId,
    isInNavItems: !!(present.navItemsById && present.navItemsById[navId]),
  });
  const url = tb.state.url;
  const struct = tb.structure || {};
  const stateScale = tb.state && typeof tb.state.scale === "number" ? tb.state.scale : 1;
  const stateTranslate = tb.state && tb.state.translate ? tb.state.translate : { x: 0, y: 0 };
  const allowDeformed = !!(tb.state && tb.state.allowDeformed);
  const filesUploadedEntry =
    state.filesUploaded &&
    (state.filesUploaded[url] ||
      Object.entries(state.filesUploaded).find(([k]) => assetKey(k) === assetKey(url))?.[1]);
  // DIAGNOSTIC: log the source-box geometry we are about to send to middleware.
  // If `allowDeformed: false` or width/height aspect doesn't match the source
  // image's aspect, the middleware's position math will visibly drift (Suspect 1).
  // eslint-disable-next-line no-console
  console.log("[AideaSmartElements] POS-DIAG sourceBox geometry", {
    sourceBoxId: boxId,
    position: box.position,
    rawStructure: {
      width: struct.width,
      widthUnit: struct.widthUnit,
      height: struct.height,
      heightUnit: struct.heightUnit,
      rotation: struct.rotation,
      horizontalFlip: struct.horizontalFlip,
      verticalFlip: struct.verticalFlip,
    },
    internalView: { scale: stateScale, translate: stateTranslate, allowDeformed },
    canvasRatio: present.globalConfig && present.globalConfig.canvasRatio,
    imageUrl: url,
  });
  return {
    trainingId: window.idealStudioTrainingId,
    navId,
    boxId,
    imageUrl: url,
    fileId: filesUploadedEntry ? filesUploadedEntry.fileId || null : null,
    generationPrompt:
      filesUploadedEntry && filesUploadedEntry.metadata && filesUploadedEntry.metadata.prompt
        ? filesUploadedEntry.metadata.prompt
        : null,
    sourceBoxGeometry: {
      position: {
        x: formatPctStringFromMaybeNumber(box.position && box.position.x),
        y: formatPctStringFromMaybeNumber(box.position && box.position.y),
        type: (box.position && box.position.type) || "absolute",
      },
      structure: {
        // pluginToolbar.structure stores width/height as numbers with the unit
        // on a sibling field (widthUnit/heightUnit). Middleware expects fully
        // formed percent strings (e.g. "30%"), so we compose them here.
        width: formatDimensionAsPctString(struct.width, struct.widthUnit),
        height: formatDimensionAsPctString(struct.height, struct.heightUnit),
        rotation: Number(struct.rotation) || 0,
        horizontalFlip: !!struct.horizontalFlip,
        verticalFlip: !!struct.verticalFlip,
      },
      internalView: {
        scale: stateScale,
        translate: stateTranslate,
        allowDeformed,
      },
    },
    canvasRatio: (present.globalConfig && present.globalConfig.canvasRatio) || 16 / 9,
    // organizationId and userId are derived server-side from the JWT in the
    // MagicElementEngineController. The studio does not need to send them.
  };
}

function middlewareUrl(suffix) {
  // Canonical middleware base URL across aidea-studio is `window.ideaLibraryRootUrl`
  // (set by environment_config.es6 from MW_API_URL env / `?baseUrl=...` query param).
  const base = window.ideaLibraryRootUrl || "";
  return `${base.replace(/\/$/, "")}/api/magic-element-engine${suffix}`;
}

/**
 * Accept either a percent string ("48.5%"), a bare number (48.5), or an
 * already-typed string and return a clean percent string.
 * Returns "0%" for null/undefined/un-parseable input so middleware DTO validation
 * (which requires `/^-?\d+(\.\d+)?%$/`) doesn't reject the request.
 */
function formatPctStringFromMaybeNumber(v) {
  if (v == null) return "0%";
  if (typeof v === "number") {
    return Number.isFinite(v) ? `${v}%` : "0%";
  }
  if (typeof v === "string") {
    if (v.endsWith("%")) return v;
    const n = Number(v);
    return Number.isFinite(n) ? `${n}%` : "0%";
  }
  return "0%";
}

/**
 * Compose `width + widthUnit` into the middleware's expected percent-string form.
 * If the source unit is "px" or anything other than "%", the box is positioned
 * in absolute pixels — middleware's positioning math assumes percent, so we still
 * emit a percent string of the numeric value (caller's responsibility to keep
 * such boxes percent-based for Aidea Smart Elements; v1.1 may pre-convert).
 */
function formatDimensionAsPctString(value, unit) {
  if (value == null) return "0%";
  if (typeof value === "string" && value.endsWith("%")) return value;
  const n = typeof value === "number" ? value : Number(String(value).replace(/[^\d.\-]/g, ""));
  if (!Number.isFinite(n)) return "0%";
  // unit is informational here; we always emit "%" because middleware's
  // assemble-layer-set node multiplies by source box width/height percentages.
  return `${n}%`;
}

/**
 * Thunk: trigger a decomposition for the box and poll until completion.
 *
 * @param {string} boxId The selected source box id.
 * @param {(message: string) => void=} toastFn Optional toast callback; receives
 *   localized error/success strings. If omitted, errors are only logged.
 */
export function decomposeBoxWithAideaSmartElements(boxId, toastFn) {
  return (dispatch, getState) => {
    let body;
    try {
      body = buildRequestBody(getState(), boxId);
    } catch (e) {
      if (toastFn) toastFn(i18n.t("aideaSmartElementsFailed"));
      // eslint-disable-next-line no-console
      console.error("[AideaSmartElements] precondition failed", e);
      return Promise.reject(e);
    }
    dispatch(
      setAideaSmartElementsProcessing(boxId, {
        jobId: null,
        percent: 0,
        step: "queued",
        message: i18n.t("aideaSmartElementsProcessing"),
      })
    );
    return apiPost(middlewareUrl("/decompose"), body, { headers: authHeaders() })
      .then(readResponse)
      .then(({ jobId }) => {
        dispatch(
          setAideaSmartElementsProcessing(boxId, {
            jobId,
            percent: 5,
            step: "starting",
            message: i18n.t("aideaSmartElementsProcessing"),
          })
        );
        return pollStatus(jobId, boxId, dispatch, toastFn);
      })
      .catch((err) => {
        dispatch(clearAideaSmartElementsProcessing(boxId));
        if (toastFn) toastFn(i18n.t("aideaSmartElementsFailed"));
        // eslint-disable-next-line no-console
        console.error("[AideaSmartElements] decompose failed", err);
        throw err;
      });
  };
}

/**
 * apiPost/apiGet return a fetch Response object. Read text then parse JSON.
 * Throws on non-2xx responses so the .catch handler can surface a localized error.
 */
async function readResponse(res) {
  if (res && typeof res.text === "function") {
    const txt = await res.text();
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}: ${txt.slice(0, 300)}`);
    }
    try {
      return JSON.parse(txt);
    } catch {
      return {};
    }
  }
  if (typeof res === "string") {
    try {
      return JSON.parse(res);
    } catch {
      return {};
    }
  }
  return res || {};
}

function pollStatus(jobId, boxId, dispatch, toastFn) {
  let polls = 0;
  let consecutiveErrors = 0;
  const pollUrl = middlewareUrl(`/jobs/${encodeURIComponent(jobId)}`);
  // eslint-disable-next-line no-console
  console.log("[AideaSmartElements] starting poll", { jobId, pollUrl });
  return new Promise((resolve, reject) => {
    const tick = () => {
      polls += 1;
      if (polls > MAX_POLLS) {
        dispatch(clearAideaSmartElementsProcessing(boxId));
        if (toastFn) toastFn(i18n.t("aideaSmartElementsFailed"));
        return reject(new Error("Polling timeout"));
      }
      apiGet(pollUrl, { headers: authHeaders() })
        .then(readResponse)
        .then((status) => {
          consecutiveErrors = 0;
          // eslint-disable-next-line no-console
          console.log("[AideaSmartElements] poll", polls, "status=", status?.status, status);
          if (status && status.status === "completed") {
            if (!status.result || !Array.isArray(status.result.layers)) {
              // eslint-disable-next-line no-console
              console.error(
                "[AideaSmartElements] completed but result.layers missing/non-array — payload:",
                status
              );
              dispatch(clearAideaSmartElementsProcessing(boxId));
              if (toastFn) toastFn(i18n.t("aideaSmartElementsFailed"));
              return reject(new Error("Bad LayerSet payload"));
            }
            // eslint-disable-next-line no-console
            console.log(
              "[AideaSmartElements] dispatching APPLY_AIDEA_SMART_ELEMENTS with",
              status.result.layers.length,
              "layers"
            );
            dispatch(applyAideaSmartElements(status.result));
            return resolve(status.result);
          }
          if (status && status.status === "failed") {
            dispatch(clearAideaSmartElementsProcessing(boxId));
            const msg =
              status.reason === "NO_SUBJECTS"
                ? i18n.t("aideaSmartElementsNoSubjects")
                : i18n.t("aideaSmartElementsFailed");
            if (toastFn) toastFn(msg);
            return reject(new Error(status.reason || "FAILED"));
          }
          dispatch(
            setAideaSmartElementsProcessing(boxId, {
              jobId,
              percent: typeof status?.progress === "number" ? status.progress : 0,
              step: status?.step,
              message: status?.message || i18n.t("aideaSmartElementsProcessing"),
            })
          );
          setTimeout(tick, POLL_INTERVAL_MS);
        })
        .catch((err) => {
          consecutiveErrors += 1;
          // eslint-disable-next-line no-console
          console.warn(
            "[AideaSmartElements] poll failed (attempt",
            polls,
            ", consecutive errors",
            consecutiveErrors,
            ")",
            err && err.message ? err.message : err
          );
          if (consecutiveErrors >= 5) {
            dispatch(clearAideaSmartElementsProcessing(boxId));
            if (toastFn) toastFn(i18n.t("aideaSmartElementsFailed"));
            return reject(err);
          }
          setTimeout(tick, POLL_INTERVAL_MS);
        });
    };
    setTimeout(tick, POLL_INTERVAL_MS);
  });
}
